import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO, isValid } from 'date-fns';
import {
  Calendar, ChevronLeft, ChevronRight, RefreshCw, Package,
  Truck, Clock, ScanSearch, Cog, Sparkles, CheckCircle,
  AlertTriangle, Phone, IndianRupee, UserCheck, XCircle, Navigation
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending:          { label: 'Pending',           color: 'bg-amber-100 text-amber-700 border-amber-200',    icon: Clock },
  pickup_assigned:  { label: 'Pickup Assigned',   color: 'bg-blue-100 text-blue-700 border-blue-200',       icon: UserCheck },
  pickup_done:      { label: 'Picked Up',         color: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: Package },
  in_process:       { label: 'In Process',        color: 'bg-violet-100 text-violet-700 border-violet-200', icon: Cog },
  process_complete: { label: 'Inspection Done',   color: 'bg-purple-100 text-purple-700 border-purple-200', icon: ScanSearch },
  cleaned:          { label: 'Cleaned',           color: 'bg-teal-100 text-teal-700 border-teal-200',       icon: Sparkles },
  out_for_delivery: { label: 'Out for Delivery',  color: 'bg-orange-100 text-orange-700 border-orange-200', icon: Truck },
  delivered:        { label: 'Delivered',         color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle },
  delivery_overdue: { label: 'Overdue',           color: 'bg-red-100 text-red-700 border-red-200',          icon: AlertTriangle },
  cancelled:        { label: 'Cancelled',         color: 'bg-slate-200 text-slate-600 border-slate-300',    icon: XCircle },
};

const safeFormat = (val, fmt) => {
  if (!val) return '—';
  try {
    const d = typeof val === 'string' ? parseISO(val) : val;
    return isValid(d) ? format(d, fmt) : '—';
  } catch { return '—'; }
};

const TrackingCard = ({ order }) => {
  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  const isOverdue = order.status === 'delivery_overdue';
  const pickupAssigned = order.assignments?.pickup;
  const deliveryAssigned = order.assignments?.delivery;

  return (
    <div className={`bg-white border rounded-2xl px-4 py-4 shadow-sm ${isOverdue ? 'border-red-200 bg-red-50/30' : 'border-slate-200'}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-2xl bg-blue-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-sm shadow-blue-100">
          {order.customer_name?.charAt(0)?.toUpperCase() || '?'}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <p className="font-bold text-slate-800 text-sm">{order.customer_name}</p>
            <span className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${cfg.color}`}>
              <Icon className="w-2.5 h-2.5" />{cfg.label}
            </span>
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-0.5 mb-2">
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <Phone className="w-3 h-3" />{order.customer_phone || '—'}
            </span>
            {order.total_clothes && (
              <span className="flex items-center gap-1 text-xs text-slate-500">
                <Package className="w-3 h-3" />{order.total_clothes} pcs
              </span>
            )}
            {order.total_amount > 0 && (
              <span className={`flex items-center gap-1 text-xs font-bold ${order.payment_status === 'paid' ? 'text-emerald-600' : 'text-red-500'}`}>
                <IndianRupee className="w-3 h-3" />{order.total_amount?.toFixed(0)} · {order.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-0.5 mb-2">
            {order.pickup_date && (
              <span className="text-[10px] text-slate-400">
                Pickup: <span className="font-semibold text-slate-600">
                  {safeFormat(order.pickup_date, 'dd MMM')}{order.pickup_time_slot ? ` · ${order.pickup_time_slot}` : ''}
                </span>
              </span>
            )}
            {order.expected_delivery_date && (
              <span className="text-[10px] text-slate-400">
                Due: <span className={`font-semibold ${isOverdue ? 'text-red-600' : 'text-orange-600'}`}>
                  {safeFormat(order.expected_delivery_date, 'dd MMM, h:mm a')}
                </span>
              </span>
            )}
            {order.delivery_date && (
              <span className="text-[10px] text-slate-400">
                Delivered: <span className="font-semibold text-emerald-600">{safeFormat(order.delivery_date, 'dd MMM, h:mm a')}</span>
              </span>
            )}
          </div>

          {order.address_line1 && (
            <p className="text-[10px] text-slate-400 truncate mb-2">{order.address_line1}{order.address_line2 ? `, ${order.address_line2}` : ''}</p>
          )}

          {/* Assignment info — read only */}
          <div className="flex flex-wrap gap-2">
            {pickupAssigned && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                <UserCheck className="w-3 h-3" /> Pickup: {pickupAssigned.delivery_boy_name}
              </span>
            )}
            {deliveryAssigned && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-orange-50 text-orange-700 border border-orange-100">
                <Truck className="w-3 h-3" /> Delivery: {deliveryAssigned.delivery_boy_name}
              </span>
            )}
            {order.status === 'cancelled' && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-slate-100 text-slate-500 border border-slate-200">
                Cancelled by {order.cancelled_by === 'customer' ? 'Customer' : 'Admin'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminDeliveryTracking = () => {
  const token = localStorage.getItem('token');
  const todayStr = format(new Date(), 'yyyy-MM-dd');

  const [date, setDate] = useState(todayStr);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/daily-order-view`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date, filter_by: 'expected_delivery_date' }
      });
      setOrders(res.data.orders || []);
    } catch {
      toast.error('Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  }, [date, token]);

  useEffect(() => { fetchOrders(); setStatusFilter('all'); }, [fetchOrders]);

  const shiftDate = (days) => {
    const d = new Date(date + 'T12:00:00');
    d.setDate(d.getDate() + days);
    setDate(format(d, 'yyyy-MM-dd'));
  };

  const statusCounts = orders.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {});

  const filtered = statusFilter === 'all' ? orders : orders.filter(o => o.status === statusFilter);

  return (
    <div className="space-y-4 pb-24 md:pb-4" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-violet-700 rounded-[2rem] px-6 py-5 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
            <Navigation className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>Delivery Tracking</h1>
            <p className="text-blue-100 text-sm">See every delivery scheduled on a given day and its live status</p>
          </div>
        </div>
      </div>

      {/* Date picker */}
      <div className="bg-white border border-slate-200 rounded-[2rem] px-5 py-4 shadow-sm">
        <div className="flex items-center gap-2">
          <button onClick={() => shiftDate(-1)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 flex-shrink-0">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm font-semibold focus:outline-none focus:border-blue-500 transition-colors" />
          </div>
          <button onClick={() => shiftDate(1)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 flex-shrink-0">
            <ChevronRight className="w-4 h-4" />
          </button>
          <button onClick={() => setDate(todayStr)} className="px-3 py-2.5 rounded-xl bg-blue-50 text-blue-600 text-xs font-bold border border-blue-100 hover:bg-blue-100 flex-shrink-0">
            Today
          </button>
          <button onClick={fetchOrders} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 flex-shrink-0">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <p className="text-sm font-bold text-slate-700 mt-2.5">
          {format(new Date(date + 'T12:00:00'), 'EEEE, dd MMMM yyyy')} · {orders.length} deliveries scheduled
        </p>
      </div>

      {/* Status filter pills */}
      {orders.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setStatusFilter('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
              statusFilter === 'all' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
            }`}>
            All <span className="ml-1 opacity-70">{orders.length}</span>
          </button>
          {Object.entries(statusCounts).sort().map(([status, count]) => {
            const cfg = STATUS_CONFIG[status];
            if (!cfg) return null;
            const Icon = cfg.icon;
            return (
              <button key={status} onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                  statusFilter === status ? cfg.color + ' scale-105 shadow-sm' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
                }`}>
                <Icon className="w-3 h-3" />
                {cfg.label}
                <span className="opacity-70">{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Orders */}
      {loading && (
        <div className="bg-white border border-slate-200 rounded-[2rem] px-5 py-12 text-center shadow-sm">
          <RefreshCw className="w-8 h-8 text-slate-300 animate-spin mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Loading...</p>
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-[2rem] px-5 py-12 text-center shadow-sm">
          <Package className="w-10 h-10 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500 font-semibold">No deliveries on this date</p>
          <p className="text-slate-400 text-sm mt-1">No orders have delivery due on this date</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest px-1">
            {filtered.length} deliver{filtered.length !== 1 ? 'ies' : 'y'}
            {statusFilter !== 'all' ? ` · ${STATUS_CONFIG[statusFilter]?.label}` : ''}
          </p>
          {filtered.map(order => <TrackingCard key={order.id} order={order} />)}
        </div>
      )}
    </div>
  );
};

export default AdminDeliveryTracking;
